import type {
  MissionDefinition,
  MissionId,
  MissionSavePayload,
  MissionStateSnapshot,
  MissionStep,
  MissionStepId
} from './MissionTypes';
import type { MissionEventBus, MissionEventType } from './MissionEventBus';

export type MissionStateListener = (snap: MissionStateSnapshot) => void;

/** Optional plugins for side effects the manager should fire when steps
 *  or missions complete. Stays read-only from the manager's point of view
 *  so the gameplay subsystems own the side effect (rewards, audio cues)
 *  rather than the manager itself. */
export interface MissionManagerHooks {
  /** Apply a reward (credits etc.) when a mission completes. */
  readonly applyReward?: (mission: MissionDefinition) => void;
  /** Fire a polished completion toast / VO line / SFX. */
  readonly onMissionCompleted?: (mission: MissionDefinition) => void;
  /** Fire a small toast when a mission becomes active. */
  readonly onMissionStarted?: (mission: MissionDefinition) => void;
  /** Fire a tiny acknowledgement when a step completes. */
  readonly onStepCompleted?: (
    mission: MissionDefinition,
    step: MissionStep
  ) => void;
}

/**
 * Drives the tutorial mission sequence:
 *  - holds the ordered list of missions
 *  - listens to MissionEventBus for matching events
 *  - advances steps and missions
 *  - exposes a read-only snapshot for the HUD/UI to render
 *
 * The manager does NOT touch render code, the HUD, the inventory, the
 * waypoint, or the save service directly — wiring is the caller's
 * responsibility (Application.ts), which keeps the manager isolated and
 * testable.
 */
export class MissionManager {
  private readonly definitions: readonly MissionDefinition[];
  private readonly bus: MissionEventBus;
  private readonly hooks: MissionManagerHooks;

  private activeMissionId: MissionId | null = null;
  private activeStepId: MissionStepId | null = null;
  private currentCount = 0;
  private readonly completedMissionIds = new Set<MissionId>();

  private readonly listeners = new Set<MissionStateListener>();
  private readonly unsubscribes: Array<() => void> = [];

  constructor(
    definitions: readonly MissionDefinition[],
    bus: MissionEventBus,
    hooks: MissionManagerHooks = {}
  ) {
    this.definitions = definitions;
    this.bus = bus;
    this.hooks = hooks;
    this.attachBusListeners();
  }

  /** Begin the first auto-start mission if nothing else is active. Safe
   *  to call after `restore()` — won't override an already-active or
   *  already-completed mission. */
  start(): void {
    if (this.activeMissionId) {
      // Already-active steps may have been satisfied externally
      // before `start()` ran (e.g. faction preset that owns the
      // upgrade the buy-step is gating). Honour skipIf even on the
      // restored entry point.
      this.maybeSkipActiveStep();
      this.emit();
      return;
    }
    this.advanceToNextAvailableMission();
    this.emit();
  }

  /** Read-only snapshot for the HUD. */
  snapshot(): MissionStateSnapshot {
    const mission = this.getActiveMission();
    const step = this.getActiveStep();
    return {
      activeMissionId: mission?.id ?? null,
      activeMissionTitle: mission?.title ?? null,
      activeMissionDescription: mission?.description ?? null,
      activeStepId: step?.id ?? null,
      objectiveText: step?.objectiveText ?? null,
      hintText: step?.hintText ?? null,
      currentCount: this.currentCount,
      requiredCount: step?.requiredCount ?? null,
      waypointTarget: step?.waypointTarget ?? null,
      completedMissionIds: Array.from(this.completedMissionIds),
      allCompleted: !mission && this.completedMissionIds.size === this.definitions.length
    };
  }

  subscribe(listener: MissionStateListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Snapshot for the save system. */
  saveSnapshot(): MissionSavePayload {
    return {
      activeMissionId: this.activeMissionId,
      activeStepId: this.activeStepId,
      currentCount: this.currentCount,
      completedMissionIds: Array.from(this.completedMissionIds)
    };
  }

  /** Restore from save. Defends against unknown ids — anything that no
   *  longer exists in the registered definitions is dropped silently so
   *  a stale save never wedges the manager. */
  restore(payload: MissionSavePayload | null | undefined): void {
    this.activeMissionId = null;
    this.activeStepId = null;
    this.currentCount = 0;
    this.completedMissionIds.clear();

    if (!payload) {
      this.emit();
      return;
    }

    for (const id of payload.completedMissionIds ?? []) {
      if (this.definitions.some((m) => m.id === id)) {
        this.completedMissionIds.add(id);
      }
    }

    if (payload.activeMissionId) {
      const mission = this.definitions.find(
        (m) => m.id === payload.activeMissionId
      );
      if (mission) {
        const step = mission.steps.find((s) => s.id === payload.activeStepId);
        // If the saved step doesn't exist anymore, restart the mission
        // from the first step rather than getting stuck.
        const resolvedStep = step ?? mission.steps[0] ?? null;
        if (resolvedStep) {
          this.activeMissionId = mission.id;
          this.activeStepId = resolvedStep.id;
          this.currentCount = step
            ? Math.max(0, payload.currentCount ?? 0)
            : 0;
        }
      }
    }

    this.emit();
  }

  dispose(): void {
    for (const unsub of this.unsubscribes) unsub();
    this.unsubscribes.length = 0;
    this.listeners.clear();
  }

  // ---- internals -----------------------------------------------------

  private attachBusListeners(): void {
    const types: MissionEventType[] = [
      'stationEntered',
      'stationExited',
      'asteroidLootCollected',
      'tradeSaleCompleted',
      'shipRefueled',
      'shopItemPurchased',
      'planetLanded',
      'surfaceShipEntered',
      'surfaceMarkerReached',
      'surfaceDepositMined',
      'gameSaved',
      'surfaceTerminalActivated',
      'inventoryValueChanged',
      'returnedToSpace',
      'systemMapOpened',
      'mapDestinationSelected',
      'jumpTargetAligned',
      'jumpChargeStarted',
      'hyperspaceJumpCompleted',
      'fuelDepleted',
      'inventoryRadioOpened',
      'rescueRequested',
      'rescueTugArrived',
      'rescueTowAccepted',
      'stationServiceOpened',
      'fuelRefilled',
      'missionTargetReached',
      'missionShipDestroyed',
      'missionItemCollected',
      'missionTerminalOpened',
      'tutorialCampaignCompleted'
    ];
    for (const type of types) {
      const unsub = this.bus.on(type, (payload) => {
        this.handleEvent(type, payload);
      });
      this.unsubscribes.push(unsub);
    }
  }

  private handleEvent(type: MissionEventType, payload: unknown): void {
    const mission = this.getActiveMission();
    const step = this.getActiveStep();
    if (!mission || !step) return;
    if (step.event !== type) return;
    if (step.predicate && !step.predicate(payload)) return;

    if (step.requiredCount && step.requiredCount > 0) {
      if (step.absoluteCount) {
        const value = Math.max(0, step.absoluteCount(payload));
        const next = Math.min(step.requiredCount, value);
        if (next === this.currentCount && next < step.requiredCount) return;
        this.currentCount = next;
        if (this.currentCount >= step.requiredCount) {
          this.completeStep(mission, step);
        } else {
          this.emit();
        }
        return;
      }
      const inc = step.increment ? Math.max(0, step.increment(payload)) : 1;
      if (inc <= 0) return;
      this.currentCount = Math.min(
        step.requiredCount,
        this.currentCount + inc
      );
      if (this.currentCount >= step.requiredCount) {
        this.completeStep(mission, step);
      } else {
        this.emit();
      }
    } else {
      this.completeStep(mission, step);
    }
  }

  private completeStep(
    mission: MissionDefinition,
    step: MissionStep
  ): void {
    this.hooks.onStepCompleted?.(mission, step);

    const idx = mission.steps.indexOf(step);
    const next = idx >= 0 ? mission.steps[idx + 1] : undefined;
    if (next) {
      this.activeStepId = next.id;
      this.currentCount = 0;
      // Newly active step may already be satisfied (skipIf) — chain
      // through synchronously without an intermediate emit.
      if (next.skipIf?.()) {
        this.completeStep(mission, next);
        return;
      }
      this.emit();
      return;
    }

    // No more steps — mission complete.
    this.completeMission(mission);
  }

  private completeMission(mission: MissionDefinition): void {
    this.completedMissionIds.add(mission.id);
    this.activeMissionId = null;
    this.activeStepId = null;
    this.currentCount = 0;
    this.hooks.applyReward?.(mission);
    this.hooks.onMissionCompleted?.(mission);

    this.advanceToNextAvailableMission();
    this.emit();
  }

  private advanceToNextAvailableMission(): void {
    for (const mission of this.definitions) {
      if (this.completedMissionIds.has(mission.id)) continue;
      if (mission.autoStart === false) continue;
      // First mission in the sequence is the only one with autoStart: true,
      // but we treat omitted/true the same so the chain keeps moving.
      const firstStep = mission.steps[0];
      if (!firstStep) continue;
      this.activeMissionId = mission.id;
      this.activeStepId = firstStep.id;
      this.currentCount = 0;
      this.hooks.onMissionStarted?.(mission);
      // Skip-through any leading steps that are already satisfied
      // (e.g. faction preset already owns the upgrade the buy step
      // is gating). The chain may also tip over the whole mission if
      // every step is skippable.
      this.maybeSkipActiveStep();
      return;
    }
  }

  /** Auto-complete the active step when its `skipIf` says it's
   *  already satisfied. `completeStep` itself recurses through
   *  consecutive skippable steps, and `completeMission` walks the
   *  chain forward, so a single check here covers any stack depth. */
  private maybeSkipActiveStep(): void {
    const mission = this.getActiveMission();
    const step = this.getActiveStep();
    if (mission && step && step.skipIf?.()) {
      this.completeStep(mission, step);
    }
  }

  private getActiveMission(): MissionDefinition | null {
    if (!this.activeMissionId) return null;
    return this.definitions.find((m) => m.id === this.activeMissionId) ?? null;
  }

  private getActiveStep(): MissionStep | null {
    const mission = this.getActiveMission();
    if (!mission || !this.activeStepId) return null;
    return mission.steps.find((s) => s.id === this.activeStepId) ?? null;
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const listener of this.listeners) listener(snap);
  }
}
